import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import { getToken } from './tokenStore';

const templateLanguageService = {
  // Get all template languages
  getAllTemplateLanguages: async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_ENDPOINTS.templateLanguage}/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template languages:', error);
      throw error;
    }
  },

  // Get template language by ID
  getTemplateLanguageById: async (id) => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_ENDPOINTS.templateLanguage}/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template language:', error);
      throw error;
    }
  },

  // Get latest template language (most recent)
  getLatestTemplateLanguage: async () => {
    return templateLanguageService.getLatestTemplateLanguageByTemplate('default');
  },

  /** Latest saved record for a specific template vendor (e.g. TransCelerate = default). */
  getLatestTemplateLanguageByTemplate: async (templateId = 'default') => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_ENDPOINTS.templateLanguage}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const rows = response.data?.data;
      if (!Array.isArray(rows) || rows.length === 0) return null;

      const forTemplate = rows.filter((row) => row.template === templateId);
      const pool = forTemplate.length > 0 ? forTemplate : rows;
      const sorted = pool.sort(
        (a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
      return sorted[0] || null;
    } catch (error) {
      console.error('Error fetching latest template language by template:', error);
      throw error;
    }
  },

  // Get confidentiality statement from template language
  getConfidentialityStatement: async () => {
    try {
      const latestTemplate = await templateLanguageService.getLatestTemplateLanguage();
      if (latestTemplate && latestTemplate.tableData) {
        const confidentialityRow = latestTemplate.tableData.find(
          row => row.title === 'Confidentiality Statement'
        );
        return confidentialityRow ? confidentialityRow.content : null;
      }
      return null;
    } catch (error) {
      console.error('Error fetching confidentiality statement:', error);
      return null;
    }
  },

  // Get all template language data for section generation
  getAllTemplateLanguageData: async () => {
    try {
      const latestTemplate = await templateLanguageService.getLatestTemplateLanguage();
      if (latestTemplate && latestTemplate.tableData) {
        // Convert table data to a more usable format
        const templateData = {};
        latestTemplate.tableData.forEach(row => {
          // row.selected !== false (not `=== true`): rows saved before this
          // field existed have no `selected` at all, and previously always
          // got included — treating "unset" as included keeps that behavior
          // instead of silently dropping every study's existing content.
          // Only an explicit uncheck (selected === false) excludes a row now.
          if (row.title && row.content && row.selected !== false) {
            templateData[row.title] = {
              content: row.content,
              userSuggestion: row.userSuggestion || ''
            };
          }
        });
        return {
          template: latestTemplate.template || 'default',
          data: templateData
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching all template language data:', error);
      return null;
    }
  },

  // Get template language data by project ID (if project-specific templates are implemented)
  getTemplateLanguageDataByProject: async (projectId) => {
    try {
      // For now, we'll use the latest template language data
      // In the future, this could be enhanced to fetch project-specific templates
      const templateData = await templateLanguageService.getAllTemplateLanguageData();
      console.log(`Template language data for project ${projectId}:`, templateData);
      return templateData;
    } catch (error) {
      console.error(`Error fetching template language data for project ${projectId}:`, error);
      return null;
    }
  }
};

export default templateLanguageService;



